import type {WizardData} from '../index'
import {parsePositiveNumber} from './allocationIds'
import {
  buildLevelCurriculumLookup,
  levelIdKey,
  lookupSubjectId,
  subjectIdKey,
} from './levelCurriculum'

type ManualData = WizardData['manualData']

const matchTeacherRecord = (a: any, b: any): boolean => {
  const empA = String(a?.employeeId ?? '').trim()
  const empB = String(b?.employeeId ?? '').trim()
  if (empA && empB && empA === empB) return true
  const nameA = (
    a?._nameInput !== undefined
      ? String(a._nameInput || '').trim()
      : `${a?.first_name || ''} ${a?.last_name || ''}`.trim()
  ).trim()
  const nameB = (
    b?._nameInput !== undefined
      ? String(b._nameInput || '').trim()
      : `${b?.first_name || ''} ${b?.last_name || ''}`.trim()
  ).trim()
  return Boolean(nameA && nameB && nameA === nameB)
}

const findTeacherId = (teacher: any, parentTeachers: any[]): number | null => {
  const existing = parsePositiveNumber(teacher?.id)
  if (existing) return existing
  const match = parentTeachers.find((p) => matchTeacherRecord(p, teacher))
  return match ? parsePositiveNumber(match?.id) : null
}

const trim = (value: unknown): string => String(value ?? '').trim()
const lower = (value: unknown): string => trim(value).toLowerCase()

/**
 * Same class: same name, and the same branch, level and curriculum wherever both rows name
 * them (response rows may carry ids or nothing there — only names are compared). "Grade 1-A"
 * exists once per branch and can exist under two curricula, so a name alone handed one
 * class's id to the other.
 */
const sameClass = (a: any, b: any): boolean => {
  const named = (value: unknown) => (typeof value === 'string' ? value.trim() : '')
  return (
    trim(a?.name) !== '' &&
    trim(a?.name) === trim(b?.name) &&
    (['branch', 'level', 'curriculum'] as const).every(
      (field) => !named(a?.[field]) || !named(b?.[field]) || lower(a[field]) === lower(b[field])
    )
  )
}

const findClassId = (cls: any, parentClasses: any[]): number | null => {
  const existing = parsePositiveNumber(cls?.id)
  if (existing) return existing
  const name = String(cls?.name ?? '').trim()
  if (!name) return null
  const match = parentClasses.find((c) => parsePositiveNumber(c?.id) && sameClass(c, cls))
  return match ? parsePositiveNumber(match?.id) : null
}

export function applyBulkImportResponseToManualData(
  baseManualData: ManualData,
  res: any,
  payloadKeys: Set<string>,
  parsedTimetable: {
    branchOptions: any[]
    levelOptions: any[]
    durationOptions: any[]
    selectedBranchId: number | null
    selectedLevelId: number | null
    selectedDurationId: number | null
  }
): ManualData {
  const src = res?.result ?? res?.data ?? res ?? {}
  const created = src?.created ?? src?.result?.created ?? {}
  const updated = src?.updated ?? src?.result?.updated ?? {}

  const nextManualData: any = {...baseManualData}

  const branchLevelIdResult = (() => {
    if (!payloadKeys.has('branch')) return null
    const rawBranches: any[] = [
      ...(Array.isArray(created?.branches) ? created.branches : []),
      ...(Array.isArray(updated?.branches) ? updated.branches : []),
      ...(Array.isArray(src?.branches) ? src.branches : []),
    ]
      // First id per name wins below, so a `renamedFrom: 'Main Branch'` entry must come after
      // the plain rows: node-api can report the same new name twice (the rename of Main Branch
      // AND the real insert) and the rename's id is Main Branch's, not the new branch's.
      .sort((a, b) => Number(Boolean(a?.renamedFrom)) - Number(Boolean(b?.renamedFrom)))
    const flatLevels: any[] = [
      ...(Array.isArray(created?.levels) ? created.levels : []),
      ...(Array.isArray(updated?.levels) ? updated.levels : []),
    ]
    if (!rawBranches.length && !flatLevels.length) return null

    const branchIds = new Map<string, number>()
    const levelIds = new Map<string, Map<string, number>>()
    const addLevel = (branchName: string, l: any) => {
      const levelId = parsePositiveNumber(l?.id)
      const levelName = String(l?.name ?? '').trim()
      if (!levelId || !levelName) return
      const levelMap = levelIds.get(branchName) ?? new Map<string, number>()
      // Keyed by curriculum when the response says which one (two curricula can share a
      // level name); the bare name otherwise, which the payload builder only trusts while
      // the name is not shared.
      const key = levelIdKey(levelName, l?.curriculum)
      if (!levelMap.has(key)) levelMap.set(key, levelId)
      levelIds.set(branchName, levelMap)
    }
    rawBranches.forEach((b: any) => {
      const id = parsePositiveNumber(b?.id)
      const name = String(b?.name ?? '').trim()
      if (!id || !name) return
      if (!branchIds.has(name)) branchIds.set(name, id)
      ;(Array.isArray(b?.levels) ? b.levels : []).forEach((l: any) => addLevel(name, l))
    })

    // node-api reports the levels it created or updated at the top level, by `branchId`, and
    // lists a branch only when the branch itself changed - so a school whose Main Branch already
    // existed got no level ids at all, and a level removed later in the same visit (a
    // deselected curriculum) had no id to delete it by: it stayed in the school. The branch
    // name comes from a known branch id, else from the one wizard branch that has the level.
    if (flatLevels.length > 0) {
      const knownBranchIds: Record<string, unknown> = {...(baseManualData.branchIdByName ?? {})}
      branchIds.forEach((id, name) => {
        knownBranchIds[name] = id
      })
      const wizardBranches = (baseManualData.branches ?? [])
        .map((entry: any) => trim(typeof entry === 'string' ? entry : entry?.name))
        .filter(Boolean)
      const lookup = buildLevelCurriculumLookup(baseManualData)
      flatLevels.forEach((l: any) => {
        const branchId = parsePositiveNumber(l?.branchId ?? l?.branch_id)
        const known = Object.entries(knownBranchIds).find(
          ([, id]) => branchId && parsePositiveNumber(id) === branchId
        )?.[0]
        let branchName = known
        if (!branchName) {
          const holders = (wizardBranches.length > 0 ? wizardBranches : ['Main Branch']).filter(
            (branch) => {
              const curricula = lookup.curriculaFor(branch, l?.name).map(lower)
              return curricula.length > 0 && (!trim(l?.curriculum) || curricula.includes(lower(l.curriculum)))
            }
          )
          if (holders.length !== 1) return
          branchName = holders[0]
          if (branchId && !branchIds.has(branchName)) branchIds.set(branchName, branchId)
        }
        addLevel(branchName, l)
      })
    }
    return branchIds.size > 0 || levelIds.size > 0 ? {branchIds, levelIds} : null
  })()

  const responseClasses: any[] = []
  const classIdToName = (() => {
    const rawClasses: any[] = [
      ...(Array.isArray(created?.classes) ? created.classes : []),
      ...(Array.isArray(updated?.classes) ? updated.classes : []),
      ...(Array.isArray(src?.classes) ? src.classes : []),
    ]
    if (!rawClasses.length) return null
    const map = new Map<number, string>()
    rawClasses.forEach((c: any) => {
      const id = parsePositiveNumber(c?.id)
      const name = String(c?.name ?? '').trim()
      if (id && name) {
        if (!map.has(id)) responseClasses.push({...c, id, name})
        map.set(id, name)
      }
    })
    return map.size > 0 ? map : null
  })()

  // Curriculum of a class id: the response row's, else the wizard row with that id, else the
  // only curriculum that has a class of that name. Subject ids are keyed per curriculum.
  const classCurriculumFor = (classId: number | null, className: string): string => {
    const own = responseClasses.find((c) => c.id === classId)
    if (trim(own?.curriculum)) return trim(own.curriculum)
    const rows = (baseManualData.classes ?? []) as any[]
    const byId = classId ? rows.find((c) => parsePositiveNumber(c?.id) === classId) : undefined
    if (trim(byId?.curriculum)) return trim(byId.curriculum)
    const curricula = new Set(
      rows
        .filter((c) => trim(c?.name) === className && trim(c?.curriculum))
        .map((c) => trim(c.curriculum))
    )
    return curricula.size === 1 ? Array.from(curricula)[0] : ''
  }
  const classNameShared = (className: string): boolean =>
    new Set(
      ((baseManualData.classes ?? []) as any[])
        .filter((c) => trim(c?.name) === className && trim(c?.curriculum))
        .map((c) => lower(c.curriculum))
    ).size > 1

  // Branch of a class id: the wizard row that holds the id (`classRows` is read after this
  // response's class ids were handed out), else the only branch with a class of that name and
  // curriculum. The response's class rows carry no branch, and every branch has its own
  // "Grade 1-A" — subject ids are keyed per branch.
  const classBranchFor = (
    classRows: any[],
    classId: number | null,
    className: string,
    curriculum: string
  ): string => {
    const byId = classId ? classRows.find((c) => parsePositiveNumber(c?.id) === classId) : undefined
    if (trim(byId?.branch)) return trim(byId.branch)
    const branches = new Set(
      classRows
        .filter(
          (c) =>
            trim(c?.name) === className &&
            trim(c?.branch) &&
            (!curriculum || !trim(c?.curriculum) || lower(c.curriculum) === lower(curriculum))
        )
        .map((c) => trim(c.branch))
    )
    return branches.size === 1 ? Array.from(branches)[0] : ''
  }
  const classNameInBranches = (classRows: any[], className: string): boolean =>
    new Set(
      classRows
        .filter((c) => trim(c?.name) === className && trim(c?.branch))
        .map((c) => lower(c.branch))
    ).size > 1

  const buildSubjectIds = (classRows: any[]) => {
    if (!payloadKeys.has('subjects')) return null
    const rawSubjects: any[] = [
      ...(Array.isArray(created?.subjects) ? created.subjects : []),
      ...(Array.isArray(updated?.subjects) ? updated.subjects : []),
      ...(Array.isArray(src?.subjects) ? src.subjects : []),
    ]
    if (!rawSubjects.length) return null

    const cidToName = new Map<number, string>()
    if (classIdToName) classIdToName.forEach((name, id) => cidToName.set(id, name))
    ;(baseManualData.classes ?? []).forEach((cls: any) => {
      const id = parsePositiveNumber(cls?.id)
      const name = String(cls?.name ?? '').trim()
      if (id && name && !cidToName.has(id)) cidToName.set(id, name)
    })

    const byNameAndClass = new Map<string, number>()
    const byName = new Map<string, number>()
    rawSubjects.forEach((s: any) => {
      const id = parsePositiveNumber(s?.id)
      const name = String(s?.name ?? '').trim()
      if (!id || !name) return
      const classId = parsePositiveNumber(s?.classId ?? s?.class_id)
      const className = classId ? cidToName.get(classId) : undefined
      if (className) {
        const curriculum = trim(s?.curriculum) || classCurriculumFor(classId, className)
        // The response names classes only: with the class name under two curricula and no
        // way to tell whose row this is, keep no id rather than a bare key either class
        // would read. The next resume-data brings the ids keyed by curriculum.
        if (!curriculum && classNameShared(className)) return
        // Same for the branch: a branch-less key handed one branch's id to the other's rows,
        // which then went out as "already saved" and never reached the server.
        const branch = classBranchFor(classRows, classId, className, curriculum)
        if (!branch && classNameInBranches(classRows, className)) return
        const key = subjectIdKey(name, className, curriculum, branch)
        if (!byNameAndClass.has(key)) byNameAndClass.set(key, id)
      }
      if (!byName.has(name)) byName.set(name, id)
    })
    return {
      byNameAndClass: byNameAndClass.size > 0 ? byNameAndClass : null,
      byName: byName.size > 0 ? byName : null,
    }
  }

  const teacherIdResult = (() => {
    if (!payloadKeys.has('teachers')) return null
    const rawTeachers: any[] = [
      ...(Array.isArray(created?.teachers) ? created.teachers : []),
      ...(Array.isArray(updated?.teachers) ? updated.teachers : []),
      ...(Array.isArray(src?.teachers) ? src.teachers : []),
    ]
    if (!rawTeachers.length) return null

    const byEmployeeId = new Map<string, number>()
    const byDisplayName = new Map<string, number>()
    rawTeachers.forEach((t: any) => {
      const id = parsePositiveNumber(t?.id)
      if (!id) return
      const empId = String(t?.employeeId ?? t?.employee_id ?? t?.last_name ?? '').trim()
      const displayName =
        String(t?.first_name ?? '').trim() ||
        [t?.first_name, t?.last_name]
          .map((s: any) => String(s ?? '').trim())
          .filter(Boolean)
          .join(' ')
      if (empId && !byEmployeeId.has(empId)) byEmployeeId.set(empId, id)
      if (displayName && !byDisplayName.has(displayName)) byDisplayName.set(displayName, id)
      // A server that splits the name (`result.teachers`: first + last) still matches the row's
      // full name as read.
      const fullName = [t?.first_name, t?.last_name]
        .map((s: any) => String(s ?? '').trim())
        .filter(Boolean)
        .join(' ')
      if (fullName && !byDisplayName.has(fullName)) byDisplayName.set(fullName, id)
    })
    return byEmployeeId.size > 0 || byDisplayName.size > 0 ? {byEmployeeId, byDisplayName} : null
  })()

  if (branchLevelIdResult) {
    const {branchIds, levelIds} = branchLevelIdResult
    // `branches` stays a plain string list — every screen renders and edits it as names
    // (turning a synced entry into `{name, id}` showed "[object Object]" chips). Server ids
    // live in `branchIdByName`, mirroring `branchLevelIdMap`; the payload builder reads it.
    const prevBranchIds = (nextManualData.branchIdByName as Record<string, number>) ?? {}
    const mergedBranchIds: Record<string, number> = {...prevBranchIds}
    branchIds.forEach((id, name) => {
      mergedBranchIds[name] = id
    })
    nextManualData.branchIdByName = mergedBranchIds
    nextManualData.branches = (baseManualData.branches ?? []).map((branch: any) =>
      String(typeof branch === 'string' ? branch : (branch?.name ?? '')).trim()
    )
    const prevLevelMap =
      (nextManualData.branchLevelIdMap as Record<string, Record<string, number>>) ?? {}
    const mergedLevelMap: Record<string, Record<string, number>> = {...prevLevelMap}
    levelIds.forEach((levelMap, branchName) => {
      const existing = mergedLevelMap[branchName] ?? {}
      levelMap.forEach((levelId, levelName) => {
        existing[levelName] = levelId
      })
      mergedLevelMap[branchName] = existing
    })
    nextManualData.branchLevelIdMap = mergedLevelMap
  }

  if (classIdToName) {
    // Each returned class id goes to one wizard row: the first unassigned row that is the same
    // class (name, and branch/level/curriculum where the response carries them).
    const usedIds = new Set<number>(
      (baseManualData.classes ?? [])
        .map((cls: any) => parsePositiveNumber(cls?.id))
        .filter((id): id is number => id !== null)
    )
    nextManualData.classes = (baseManualData.classes ?? []).map((cls: any) => {
      const existingId = findClassId(cls, baseManualData.classes ?? [])
      if (existingId) return {...cls, id: existingId}
      const match = responseClasses.find((c) => !usedIds.has(c.id) && sameClass(c, cls))
      if (match) {
        usedIds.add(match.id)
        return {...cls, id: match.id}
      }
      return cls
    })
  } else {
    nextManualData.classes = (baseManualData.classes ?? []).map((cls: any) => {
      const id = findClassId(cls, baseManualData.classes ?? [])
      return id ? {...cls, id} : cls
    })
  }

  if (teacherIdResult) {
    const {byEmployeeId, byDisplayName} = teacherIdResult
    nextManualData.teachers = (baseManualData.teachers ?? []).map((teacher: any) => {
      const existingId = findTeacherId(teacher, baseManualData.teachers ?? [])
      if (existingId) return {...teacher, id: existingId}
      const empId = String(teacher?.employeeId ?? '').trim()
      const displayName = [teacher?.first_name, teacher?.last_name]
        .map((s: any) => String(s ?? '').trim())
        .filter(Boolean)
        .join(' ')
      const nameInput =
        teacher?._nameInput !== undefined ? String(teacher._nameInput || '').trim() : displayName
      const id =
        (empId ? byEmployeeId.get(empId) : undefined) ??
        (nameInput ? byDisplayName.get(nameInput) : undefined) ??
        (displayName ? byDisplayName.get(displayName) : undefined)
      return id ? {...teacher, id} : teacher
    })
  } else {
    nextManualData.teachers = (baseManualData.teachers ?? []).map((teacher: any) => {
      const id = findTeacherId(teacher, baseManualData.teachers ?? [])
      return id ? {...teacher, id} : teacher
    })
  }

  // After the class ids above: a subject's class (and so its branch) is found by that id.
  const subjectIdResult = buildSubjectIds((nextManualData.classes ?? []) as any[])
  if (subjectIdResult) {
    const {byNameAndClass, byName} = subjectIdResult
    if (byNameAndClass) {
      const prevMap = (nextManualData.subjectIdByNameAndClass as Record<string, number>) ?? {}
      const merged: Record<string, number> = {...prevMap}
      byNameAndClass.forEach((id, key) => {
        merged[key] = id
      })
      nextManualData.subjectIdByNameAndClass = merged
    }
    const idByKey = byNameAndClass ? Object.fromEntries(byNameAndClass) : null
    nextManualData.subjects = (baseManualData.subjects ?? []).map((subj: any) => {
      if (parsePositiveNumber(subj?.id)) return subj
      const name = String(subj?.name ?? '').trim()
      const className = String(subj?.class ?? '').trim()
      if (idByKey && className) {
        const id = lookupSubjectId(
          idByKey,
          name,
          className,
          subj?.curriculum,
          subj?.branch
        )
        if (id) return {...subj, id}
      }
      // A same-named subject of another class (or branch) is never this row's id: the server
      // updates by id and ignores the class, and the row would be skipped as already saved.
      if (byName && !className) {
        const id = byName.get(name)
        if (id) return {...subj, id}
      }
      return subj
    })
  }

  if (parsedTimetable.branchOptions.length > 0 || parsedTimetable.levelOptions.length > 0) {
    nextManualData.timetableBranchOptions =
      parsedTimetable.branchOptions.length > 0
        ? parsedTimetable.branchOptions
        : (baseManualData.timetableBranchOptions ?? [])
    nextManualData.timetableLevelOptions =
      parsedTimetable.levelOptions.length > 0
        ? parsedTimetable.levelOptions
        : (baseManualData.timetableLevelOptions ?? [])
    nextManualData.timetableDurationOptions = parsedTimetable.durationOptions
    nextManualData.selectedTimetableBranchId =
      parsedTimetable.selectedBranchId ?? baseManualData.selectedTimetableBranchId ?? null
    nextManualData.selectedTimetableLevelId =
      parsedTimetable.selectedLevelId ?? baseManualData.selectedTimetableLevelId ?? null
    nextManualData.selectedTimetableDurationId =
      parsedTimetable.selectedDurationId ?? baseManualData.selectedTimetableDurationId ?? null
  }

  return nextManualData as ManualData
}
